import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FILTER_VIEWS_KEY } from './constants';
import { updateFilterView } from './filterViews';
import type {
  UpdateFilterViewPayload,
  FilterView,
  FilterViewsResponse,
} from './filterViews.types';

/**
 * Hook to update an existing filter view
 */
export const useUpdateFilterView = (pageIdentifier: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFilterViewPayload }) =>
      updateFilterView(pageIdentifier, id, data),
    onSuccess: (
      updatedView: FilterView,
      { data }: { id: string; data: UpdateFilterViewPayload },
    ) => {
      // If isDefault is being set, invalidate to refetch with new ordering from backend
      // Backend reorders views when default changes (default always at index 0)
      // We only include isDefault in the payload when explicitly setting/unsetting it
      if (data.isDefault !== undefined) {
        queryClient.invalidateQueries({
          queryKey: [FILTER_VIEWS_KEY, pageIdentifier],
        });
      } else {
        // For other updates (name, filters, sortOrder), use optimistic update
        queryClient.setQueryData<FilterViewsResponse>(
          [FILTER_VIEWS_KEY, pageIdentifier],
          (oldData) => {
            if (!oldData) return [updatedView];
            return oldData.map((view) =>
              view.filterViewId === updatedView.filterViewId
                ? updatedView
                : view,
            );
          },
        );
      }
    },
  });
};
